import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Input,
  Tooltip,
} from '@heroui/react'
import { useMutation } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Globe,
  Search,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import URLChipInput from './URLChipInput'

// Build sitemap tree for a specific domain
function buildDomainSitemap(urls, domain) {
  const domainUrls = urls.filter((url) => {
    try {
      return new URL(url).hostname === domain
    }
    catch {
      return false
    }
  })

  const paths = domainUrls.map((url) => {
    try {
      const urlObj = new URL(url)
      return {
        url,
        path: urlObj.pathname,
        fullPath: urlObj.pathname + urlObj.search,
      }
    }
    catch {
      return { url, path: '/', fullPath: '/' }
    }
  })

  return paths.sort((a, b) => a.path.localeCompare(b.path))
}

function DomainCard({ domain, onUrlsChange, selectedUrls }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [sitemapUrls, setSitemapUrls] = useState([])
  const [localSelectedUrls, setLocalSelectedUrls] = useState(() => new Set(selectedUrls))
  const [searchTerm, setSearchTerm] = useState('')

  const domainUrls = useMemo(() => {
    return selectedUrls.filter((url) => {
      try {
        return new URL(url).hostname === domain
      }
      catch {
        return false
      }
    })
  }, [selectedUrls, domain])

  // Fetch sitemap for this domain
  const { mutate: fetchDomainSitemap, isPending: isLoading } = useMutation({
    mutationFn: () => {
      // Use just the hostname with https protocol
      const hostnameUrl = `https://${domain}`

      return $fetch('/v1/knowledge/crawl', {
        method: 'POST',
        body: { url: hostnameUrl },
      })
    },
    onSuccess: ({ data }) => {
      setSitemapUrls(data.urls || [])
      setIsExpanded(true)
    },
    onError: (error) => {
      toast.error(`Failed to fetch sitemap for ${domain}`)
      console.error(error)
    },
  })

  const handleDiscoverPages = () => {
    if (sitemapUrls.length > 0) {
      setIsExpanded(!isExpanded)
    }
    else {
      fetchDomainSitemap()
    }
  }

  const handleUrlToggle = (url, selected) => {
    const newSelected = new Set(localSelectedUrls)
    if (selected) {
      newSelected.add(url)
    }
    else {
      newSelected.delete(url)
    }
    setLocalSelectedUrls(newSelected)

    // Update parent with new URL list
    const allSelected = new Set(selectedUrls)
    if (selected) {
      allSelected.add(url)
    }
    else {
      allSelected.delete(url)
    }
    onUrlsChange(Array.from(allSelected))
  }

  const handleSelectAll = () => {
    const newSelected = new Set([...selectedUrls, ...sitemapUrls])
    onUrlsChange(Array.from(newSelected))
    setLocalSelectedUrls(new Set(sitemapUrls))
  }

  const handleSelectNone = () => {
    const remainingUrls = selectedUrls.filter((url) => {
      try {
        return new URL(url).hostname !== domain
      }
      catch {
        return true
      }
    })
    onUrlsChange(remainingUrls)
    setLocalSelectedUrls(new Set())
  }

  const sitemapPaths = useMemo(() => {
    const allPaths = buildDomainSitemap(sitemapUrls, domain)
    if (!searchTerm)
      return allPaths

    return allPaths.filter(({ url, fullPath }) =>
      url.toLowerCase().includes(searchTerm.toLowerCase())
      || fullPath.toLowerCase().includes(searchTerm.toLowerCase()),
    )
  }, [sitemapUrls, domain, searchTerm])

  const selectedSitemapCount = sitemapUrls.filter(url => localSelectedUrls.has(url)).length
  const filteredSelectedCount = sitemapPaths.filter(({ url }) => localSelectedUrls.has(url)).length

  return (
    <Card className="w-full bg-gray-50" radius="none">
      <CardHeader className="pb-2">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-gray-600" />
            <div>
              <h4 className="text-base font-medium text-gray-900">{domain}</h4>
              <p className="text-xs text-gray-500">
                {domainUrls.length}
                {' '}
                URL
                {domainUrls.length !== 1 ? 's' : ''}
                {' '}
                selected
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {domainUrls.length > 0 && (
              <Chip size="sm" color="success" variant="flat">
                {domainUrls.length}
              </Chip>
            )}

            <Button
              variant="bordered"
              size="sm"
              onPress={handleDiscoverPages}
              isLoading={isLoading}
              radius="none"
              className="border-gray-300 text-gray-700"
              startContent={
                !isLoading && (
                  isExpanded
                    ? <ChevronDown className="h-3 w-3" />
                    : (
                        <ChevronRight className="h-3 w-3" />
                      )
                )
              }
            >
              {isLoading ? 'Loading...' : isExpanded ? 'Hide' : 'Discover'}
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && sitemapUrls.length > 0 && (
        <CardBody className="pt-0">
          <div className="space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className={`
                absolute top-1/2 left-3 h-3 w-3 -translate-y-1/2 text-gray-400
              `}
              />
              <Input
                placeholder="Search pages..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                size="sm"
                radius="none"
                className="pl-10"
                classNames={{
                  input: 'bg-white text-sm',
                  inputWrapper: 'border-gray-200',
                }}
              />
            </div>

            {/* Controls */}
            <div className={`
              flex items-center justify-between border border-gray-200
              bg-gray-50 px-3 py-2
            `}
            >
              <span className="text-sm text-gray-700">
                {searchTerm
                  ? (
                      <>
                        Showing
                        {' '}
                        {sitemapPaths.length}
                        {' '}
                        of
                        {' '}
                        {sitemapUrls.length}
                        {' '}
                        pages •
                        {' '}
                        {filteredSelectedCount}
                        {' '}
                        selected
                      </>
                    )
                  : (
                      <>
                        Found
                        {' '}
                        {sitemapUrls.length}
                        {' '}
                        pages •
                        {' '}
                        {selectedSitemapCount}
                        {' '}
                        selected
                      </>
                    )}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="light" onPress={handleSelectAll} radius="none">
                  Select All
                </Button>
                <Button size="sm" variant="light" onPress={handleSelectNone} radius="none">
                  Select None
                </Button>
              </div>
            </div>

            {/* URL List */}
            <div className="max-h-64 space-y-1 overflow-y-auto">
              {sitemapPaths.map(({ url, path: _path, fullPath }) => {
                const isSelected = localSelectedUrls.has(url)
                return (
                  <div
                    key={url}
                    className={`
                      group flex items-center gap-3 rounded border p-2
                      transition-colors
                      hover:bg-gray-50
                      ${
                  isSelected
                    ? 'border-blue-200 bg-blue-50'
                    : `border-gray-200`
                  }
                    `}
                  >
                    <div
                      className={`
                        h-4 w-4 cursor-pointer rounded border-2
                        transition-colors
                        ${
                  isSelected
                    ? 'border-blue-600 bg-blue-600'
                    : `border-gray-300`
                  }
                      `}
                      onClick={() => handleUrlToggle(url, !isSelected)}
                    >
                      {isSelected && (
                        <div className={`
                          flex h-full w-full items-center justify-center
                        `}
                        >
                          <div className="h-2 w-2 rounded-sm bg-white"></div>
                        </div>
                      )}
                    </div>

                    <div
                      className="min-w-0 flex-1 cursor-pointer"
                      onClick={() => handleUrlToggle(url, !isSelected)}
                    >
                      <div className="truncate font-mono text-sm text-gray-900" title={url}>
                        {fullPath === '/' ? '/' : fullPath}
                      </div>
                    </div>

                    <Tooltip content="Open in new tab">
                      <Button
                        isIconOnly
                        variant="light"
                        size="sm"
                        onPress={() => window.open(url, '_blank')}
                        className={`
                          text-gray-600 opacity-0 transition-opacity
                          group-hover:opacity-100
                          hover:bg-white
                        `}
                        radius="none"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    </Tooltip>
                  </div>
                )
              })}
            </div>
          </div>
        </CardBody>
      )}
    </Card>
  )
}

export default function DomainURLCollector({ urls, onUrlsChange, selectedUrls, onSelectedUrlsChange }) {
  const domains = useMemo(() => {
    const domainSet = new Set()
    urls.forEach((url) => {
      try {
        domainSet.add(new URL(url).hostname)
      }
      catch {
        // Invalid URL, skip
      }
    })
    return Array.from(domainSet).sort()
  }, [urls])

  // Automatically add manually entered URLs to selected list
  const handleUrlsChange = (newUrls) => {
    onUrlsChange(newUrls)

    // Add any new URLs to the selected list automatically
    const existingSelected = new Set(selectedUrls)
    const hasNewUrls = newUrls.some(url => !existingSelected.has(url))

    if (hasNewUrls) {
      const allSelected = new Set([...selectedUrls, ...newUrls])
      onSelectedUrlsChange(Array.from(allSelected))
    }
  }

  return (
    <div className="space-y-6">
      {/* URL Input */}
      <div>
        <label className="mb-2 block text-sm font-medium">Website URLs</label>
        <URLChipInput
          urls={urls}
          onChange={handleUrlsChange}
          placeholder="Enter website URLs like https://docs.bland.ai"
        />
        <p className="mt-2 text-xs text-gray-500">
          URLs you add will be automatically selected for scraping.
        </p>
      </div>

      {/* Domain Cards - Less Prominent */}
      {domains.length > 0 && (
        <div>
          <h4 className="mb-3 text-base font-medium text-gray-700">Optional: Discover More Pages</h4>
          <div className="space-y-3">
            {domains.map(domain => (
              <DomainCard
                key={domain}
                domain={domain}
                onUrlsChange={onSelectedUrlsChange}
                selectedUrls={selectedUrls}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
